import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileStickyBar from "../components/MobileStickyBar";
import Seo from "../components/Seo";
import QuoteForm from "../components/QuoteForm";
import { COMPANY, api, imgUrl } from "../lib/site";
import { useLang } from "../i18n/LanguageContext";

const SEO_MAP = {
  "tomrer-halden": { title: "Tømrer i Halden – snekker & tømrerarbeid | NOUROZI EBYGG & ANLEGG", desc: "Erfaren tømrer og snekker i Halden. Vi utfører tømrerarbeid, nybygg, tilbygg, vedlikehold og rehabilitering for private og bedrifter i Halden og omegn. Be om pristilbud." },
  "oppussing-halden": { title: "Oppussing og renovering i Halden | NOUROZI EBYGG & ANLEGG", desc: "Oppussing og renovering i Halden – bad, kjøkken, stue og hele hjem. Totalpris kan avtales. Kvalitetsmaterialer og god kommunikasjon. Be om pristilbud." },
  "kjokkenmontering-halden": { title: "Kjøkkenmontering i Halden | NOUROZI EBYGG & ANLEGG", desc: "Profesjonell kjøkkenmontering i Halden – uansett leverandør. Vi monterer skap, benkeplater, hvitevarer og lister. Be om pristilbud." },
  "terrasse-halden": { title: "Terrasse i Halden – bygging av terrasse | NOUROZI EBYGG & ANLEGG", desc: "Skreddersydde terrasser i Halden og omegn. Fra fundament til rekkverk – i tre, kompositt eller kombinasjon. Be om pristilbud." },
  "takbytte-halden": { title: "Takbytte og takarbeid i Halden | NOUROZI EBYGG & ANLEGG", desc: "Takbytte og takarbeid i Halden. Nytt undertak, lekting, takstein, renner og beslag. Vedlikehold og reparasjoner. Be om pristilbud." },
  "kledning-halden": { title: "Kledning i Halden – ny ytterkledning | NOUROZI EBYGG & ANLEGG", desc: "Ny ytterkledning i Halden. Vi tar av gammel kledning, sjekker vindsperre og monterer ny fasade i valgt materiale. Be om pristilbud." },
  "dorer-vinduer-halden": { title: "Bytte av dører og vinduer i Halden | NOUROZI EBYGG & ANLEGG", desc: "Montering og utskifting av dører og vinduer i Halden. Tett innsetting og pen finish. Be om pristilbud." },
  "belegningsstein-halden": { title: "Belegningsstein i Halden – gårdsplass og uteområder | NOUROZI EBYGG & ANLEGG", desc: "Belegningsstein for gårdsplass, inngangsparti og uteområder i Halden. Solid oppbygging og god drenering. Be om pristilbud." },
  "graving-halden": { title: "Graving og grunnarbeid i Halden | NOUROZI EBYGG & ANLEGG", desc: "Graving og grunnarbeid i Halden – tomtearbeid, drenering, VA og planering. Be om pristilbud." },
  "tilbygg-halden": { title: "Tilbygg og påbygg i Halden | NOUROZI EBYGG & ANLEGG", desc: "Tilbygg og påbygg i Halden – fra idé og tegninger til nøkkelferdig resultat. Vi bistår også med planlegging. Be om pristilbud." },
};

export default function ServiceDetail() {
  const { t } = useLang();
  const { slug } = useParams();
  const { data: service, isLoading, isError } = useQuery({
    queryKey: ["service", slug],
    queryFn: async () => (await api.get(`/services/${slug}`)).data,
    retry: false,
  });
  const { data: all = [] } = useQuery({ queryKey: ["services","list"], queryFn: async () => (await api.get("/services")).data });

  if (isLoading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-[#1B3626]" /></div>;
  if (isError || !service) {
    return (<div className="min-h-screen"><Header /><div className="container-page section text-center"><h1 className="font-display text-3xl font-bold">{t("services.notFound")}</h1><Link to="/tjenester" className="btn-primary mt-6">{t("section.services.seeAll")}</Link></div><Footer /><MobileStickyBar /></div>);
  }
  const related = all.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <div className="min-h-screen">
      <Seo title={SEO_MAP[slug]?.title || `${service.title} i Halden | ${COMPANY.name}`} description={SEO_MAP[slug]?.desc || `${service.title} i Halden og omegn. ${(service.intro || service.short || "").slice(0,140)}...`} path={`/tjenester/${slug}`} image={imgUrl(service)} />
      <Header />
      <section className="relative bg-[#181B18] text-white">
        <img src={imgUrl(service)} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-page py-16 sm:py-20">
          <div className="text-xs tracking-widest uppercase text-[#E8D6B6]">{t("services.detailBadge")}</div>
          <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl">{service.title}</h1>
          <p className="mt-4 max-w-2xl text-[#E5E1D3] text-lg leading-relaxed">{service.short}</p>
        </div>
      </section>
      <section className="section">
        <div className="container-page grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {service.intro && <p className="text-[17px] leading-relaxed text-[#1C1F22]">{service.intro}</p>}
            {service.includes?.length > 0 && (<>
              <h2 className="mt-10 font-display text-2xl font-bold">{t("services.includes")}</h2>
              <div className="dot-divider mt-3" />
              <ul className="mt-4 space-y-2.5">{service.includes.map((i, idx) => <li key={idx} className="flex gap-2.5"><CheckCircle2 className="text-[#1B3626] mt-0.5 shrink-0" size={18}/>{i}</li>)}</ul>
            </>)}
            {service.process?.length > 0 && (<>
              <h2 className="mt-10 font-display text-2xl font-bold">{t("services.process")}</h2>
              <div className="dot-divider mt-3" />
              <ol className="mt-4 space-y-3 list-decimal list-inside text-[#1C1F22]">{service.process.map((p, idx) => <li key={idx}>{p}</li>)}</ol>
            </>)}
            <div className="mt-8 rounded-xl bg-[#EEF4F0] border border-[#CADCCF] p-5">
              <div className="font-display font-bold text-[#1B3626]">{t("services.area")}</div>
              <p className="text-sm text-[#1C1F22] mt-1">{t("services.areaDesc")}</p>
            </div>
            {related.length > 0 && (
              <div className="mt-10">
                <h3 className="font-display text-xl font-bold">{t("services.other")}</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/tjenester/${r.slug}`} className="flex items-center justify-between rounded-lg border border-[#E2DDD5] bg-white p-4 hover:border-[#1B3626]" data-testid={`related-${r.slug}`}>
                      <span className="font-semibold">{r.title}</span><ArrowRight size={16} />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside className="lg:col-span-5">
            <div className="sticky top-24 bg-white border border-[#E2DDD5] rounded-2xl p-6">
              <h3 className="font-display text-xl font-bold">{t("services.quoteBoxTitle")}</h3>
              <p className="text-sm text-[#555E68] mt-1">{t("services.quoteBoxDesc")}</p>
              <div className="mt-5"><QuoteForm compact defaultService={service.title} /></div>
              <div className="mt-4 text-center text-sm text-[#555E68]">{t("services.or")}</div>
              <a href={`tel:${COMPANY.phoneClean}`} className="btn-primary w-full mt-3" data-testid="svc-call-btn"><Phone size={16}/> {t("hero.callBtn")} {COMPANY.phone}</a>
            </div>
          </aside>
        </div>
      </section>
      <Footer /><MobileStickyBar />
    </div>
  );
}
